import { Text, View } from '@react-pdf/renderer'
import {
  AutoComplete,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
} from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import compact from 'lodash/compact'
import { AddendumPages } from 'pages/reporting/reports/rug/components/AddendumPages'
import { SectionStatsTable } from 'pages/reporting/reports/rug/components/SectionStatsTable'
import { useComponentNominalRangeString } from 'pages/reporting/reports/rug/hooks/useComponentNominalRangeString'
import {
  Resolution,
  RugReportSectionId,
  SummarySectionData,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import {
  ComponentId,
  ReportCoverageScopeOptions,
} from 'pages/reporting/reports/rug/types'
import { PageFooter } from 'pages/reporting/shared/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'pages/reporting/shared/pdfGeneration/components/shared/PageHeader'
import { ReportPage } from 'pages/reporting/shared/pdfGeneration/components/shared/ReportPage'
import { PDFStyles } from 'pages/reporting/shared/pdfGeneration/styles/PDFStyles'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import { PdfProps, ReportSection } from 'pages/reporting/shared/types'
import React, { useMemo } from 'react'
import {
  GeckoAssetModelType,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'
import { autocompleteFilterOption } from 'utils/fde/antD'

const RESOLUTION_UNIT_OPTIONS = [
  {
    value: GeckoMeasurementUnit.Tubes.id,
    label: GeckoMeasurementUnit.Tubes.name,
  },
  {
    value: GeckoMeasurementUnit.Inches.id,
    label: GeckoMeasurementUnit.Inches.name,
  },
  {
    value: GeckoMeasurementUnit.Millimeters.id,
    label: GeckoMeasurementUnit.Millimeters.name,
  },
  {
    value: GeckoMeasurementUnit.Feet.id,
    label: GeckoMeasurementUnit.Feet.name,
  },
  {
    value: GeckoMeasurementUnit.Meters.id,
    label: GeckoMeasurementUnit.Meters.name,
  },
]

const requiredSummaryPageFields = [
  'summarySectionData.inspectionResolution.width',
  'summarySectionData.inspectionResolution.height',
  'summarySectionData.inspectionResolution.widthUnit',
  'summarySectionData.inspectionResolution.heightUnit',
  'summarySectionData.deliverableResolution.width',
  'summarySectionData.deliverableResolution.height',
  'summarySectionData.deliverableResolution.widthUnit',
  'summarySectionData.deliverableResolution.heightUnit',
  'summarySectionData.coverageScope',
  'summarySectionData.corrosionBLUF',
]

const CORROSION_BLUF_TEMPLATES = {
  'Mode 2': `The inspection results presented in this report stem from the usage of a Mode 2 (interface-to-echo) measurement approach. The selection of this methodology was influenced by the lack of multiple backwall reflections, which are commonly caused by corrosion, high signal attenuation, and/or unsuitable inspection surface conditions.

The Mode 2 technique takes into account the base material as well as any potential coatings. An average thickness of the coating, determined from the inspection findings, has been calculated and a correction has been applied to the minimum thickness values presented in this report.`,
  'Surface Condition (standard)': `Inadequate inspection surface conditions, typically due to factors such as top-side corrosion, significant surface roughness, and poor coating conditions led to occasional gaps in the collected ultrasonic data.`,
  'Surface Condition (unresolvable damage)': `Inadequate inspection surface conditions, typically due to factors such as top-side corrosion, significant surface roughness, and poor coating conditions led to occasional gaps in the collected ultrasonic data and a reduced near-surface resolution.`,
  'Inclusions/Laminations': `Intermittent areas of non-metallic defects (laminations and/or inclusions) from the fabrication process were identified. These defects show no signs of in-service degradation and were excluded from the final thickness results.`,
  'Aliasing/Binning (Tanks)': `Variations in the heatmap may be observed at the intersection of adjacent shell courses with varying nominal thicknesses. These areas are indicative of data binning overlap from the adjacent shell course. The minimum thicknesses represented in this report for each shell course is exclusive of overlap readings.`,
  'Access restrictions': `Various geometrical obstructions present, restricting access for data collection (ex. Nozzles, manways, stairs, supports, conduit, etc.).`,
  'Weld Line Data Omission': `Data points collected 6" above and below horizontal weld lines are omitted from low readings table due to potential localization errors.`,
} as const

interface AssetSectionStats {
  sectionName: string
  minThickness: GeckoMeasurement
  averageThickness: GeckoMeasurement
  wallLossPercent: GeckoMeasurement
  nominalThickness?: GeckoMeasurement
  tMin?: GeckoMeasurement
}

// Helper function to calculate wall loss percentage
const calculateWallLossPercent = (
  minThickness: GeckoMeasurement,
  nominalThickness: GeckoMeasurement | undefined
): GeckoMeasurement => {
  if (!minThickness || !nominalThickness) {
    return GeckoMeasurement.CreateFromValue(0, GeckoMeasurementUnit.Percent)
  }
  if (nominalThickness.num === 0) {
    return GeckoMeasurement.CreateFromValue(
      Number.NaN,
      GeckoMeasurementUnit.Percent
    )
  }

  const minThicknessConverted = minThickness.convertTo(nominalThickness.unit)

  const lossPercent =
    100 -
    ((nominalThickness.num - minThicknessConverted.num) /
      nominalThickness.num) *
      100

  return GeckoMeasurement.CreateFromValue(
    Math.max(0, lossPercent),
    GeckoMeasurementUnit.Percent
  )
}

// Component for rendering individual section statistics
const SectionStatsText: React.FC<AssetSectionStats> = ({
  sectionName,
  minThickness,
  averageThickness,
  wallLossPercent,
  nominalThickness,
}) => {
  const { formatMeasurement } = useRugReportOps()

  return (
    <View style={{ marginBottom: 12 }}>
      <Text
        style={{
          fontSize: 10,
          fontWeight: 'medium',
          marginBottom: 4,
          color: '#2e5266',
        }}>
        {sectionName}
      </Text>
      <View style={{ paddingLeft: 8 }}>
        <Text style={{ fontSize: 8, marginBottom: 4 }}>
          • The minimum thickness detected was{' '}
          {formatMeasurement({
            measurement: minThickness,
            variant: 'narrow',
            minimumFractionDigits: 3,
          })}{' '}
          with an average thickness of{' '}
          {formatMeasurement({
            measurement: averageThickness,
            variant: 'narrow',
            minimumFractionDigits: 3,
          })}
          .
        </Text>
        <Text style={{ fontSize: 8, marginBottom: 4 }}>
          • This minimum thickness represents a{' '}
          {formatMeasurement({
            measurement: wallLossPercent,
            variant: 'narrow',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{' '}
          wall loss
          {nominalThickness
            ? ` from the nominal thickness of ${formatMeasurement({
                measurement: nominalThickness,
                variant: 'narrow',
                minimumFractionDigits: 3,
              })}`
            : ''}
          .
        </Text>
      </View>
    </View>
  )
}

const RenderPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useRugReportOps()

  const {
    statsBySection,
    statsByComponent,
    inspectionMetadata,
    componentToNominalRange,
    selectedComponents,
  } = useRugReportOps()
  const { formatMeasurement } = useRugReportOps()

  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const summaryData = useMemo(
    () => formData.summarySectionData,
    [formData.summarySectionData]
  )

  // Get nominal range from the component data
  const nominalRangeStr = useComponentNominalRangeString(
    componentToNominalRange
  )

  const assetSectionStats: AssetSectionStats[] = useMemo(() => {
    return (
      compact(
        selectedComponents?.map((component) => {
          const statsForWholeComponent = statsByComponent?.get(
            component.id as ComponentId
          )
          const statsForComponentSections = statsBySection?.get(
            component.id as ComponentId
          )

          if (statsForComponentSections) {
            const assetSectionStats: AssetSectionStats[] = []
            statsForComponentSections.forEach((sectionStats) => {
              let minimumThickness = GeckoMeasurement.CreateFromValue(
                Number.POSITIVE_INFINITY,
                GeckoMeasurementUnit.Inches
              )
              sectionStats.minThicknesses.forEach((currentMinThickness) => {
                if (!sectionStats.nominalThickness) {
                  return
                }

                if (currentMinThickness.thickness.lt(minimumThickness)) {
                  minimumThickness = currentMinThickness.thickness
                }
              })
              assetSectionStats.push({
                sectionName: sectionStats.section.name,
                minThickness: minimumThickness,
                averageThickness: sectionStats.averageThickness,
                wallLossPercent: calculateWallLossPercent(
                  minimumThickness,
                  sectionStats.nominalThickness
                ),
                nominalThickness: sectionStats.nominalThickness,
                tMin: sectionStats.tMin,
              })
            })
            return assetSectionStats
          } else if (statsForWholeComponent) {
            let minimumThickness = GeckoMeasurement.CreateFromValue(
              Number.POSITIVE_INFINITY,
              GeckoMeasurementUnit.Inches
            )
            statsForWholeComponent.minThicknesses.forEach(
              (currentMinThickness) => {
                if (!statsForWholeComponent.nominalThickness) {
                  return
                }

                if (currentMinThickness.thickness.lt(minimumThickness)) {
                  minimumThickness = currentMinThickness.thickness
                }
              }
            )
            return {
              sectionName: component.data.name,
              minThickness: minimumThickness,
              averageThickness: statsForWholeComponent.averageThickness,
              wallLossPercent: calculateWallLossPercent(
                minimumThickness,
                statsForWholeComponent.nominalThickness
              ),
              nominalThickness: statsForWholeComponent.nominalThickness,
              tMin: statsForWholeComponent.tMin,
            }
          }

          return undefined
        })
      ).flat() ?? []
    )
  }, [statsBySection, statsByComponent, selectedComponents])

  // Calculate overall maximum wall loss from all components
  const maxWallLossPercent = useMemo(() => {
    const maxWallLoss = GeckoMeasurement.CreateFromValue(
      0,
      GeckoMeasurementUnit.Percent
    )

    return assetSectionStats.reduce((max, sectionStats) => {
      if (sectionStats.wallLossPercent.gt(max)) {
        return sectionStats.wallLossPercent
      }
      return max
    }, maxWallLoss)
  }, [assetSectionStats])

  const formatResolution = (
    resolution: Resolution | undefined
  ): string | null => {
    if (!resolution || !resolution.width || !resolution.height) {
      return null
    }

    const width = GeckoMeasurement.CreateFromValue(
      resolution.width,
      GeckoMeasurementUnit.GetOrCreate(resolution.widthUnit)
    )

    const height = GeckoMeasurement.CreateFromValue(
      resolution.height,
      GeckoMeasurementUnit.GetOrCreate(resolution.heightUnit)
    )

    const widthStr = formatMeasurement({
      measurement: width,
      variant: 'narrow',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    })

    const heightStr = formatMeasurement({
      measurement: height,
      variant: 'narrow',
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    })

    if (resolution.angles) {
      return `${widthStr} x ${resolution.angles} angles x ${heightStr}`
    }

    return `${widthStr} x ${heightStr}`
  }

  const totalUTReadingsStr: string = inspectionMetadata.reported_ascan_count
    ? `A total of ${inspectionMetadata.reported_ascan_count?.toLocaleString()} UT readings were collected on the ${mainInfo.assetType} (${mainInfo.assetName}) with RUG.`
    : `UT readings were collected on the ${mainInfo.assetType} (${mainInfo.assetName}) with RUG.`

  return (
    <>
      <ReportPage
        id='inspectionSummary'
        tracker={{
          sectionId: RugReportSectionId.SUMMARY,
          onRender,
          registerOnce: false,
        }}
        header={<PageHeader title='Inspection Summary' />}
        footer={
          <PageFooter
            jobNumber={mainInfo.assetId}
            revisionNumber={formData.revisionMetadata.revisionNumber}
          />
        }
        contentStyle={PDFStyles.content}>
        <View style={{ marginBottom: 16 }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: 'bold',
              marginBottom: 8,
              color: '#2e5266',
            }}>
            Inspection Scope:
          </Text>

          <Text style={{ fontSize: 10, marginBottom: 8 }}>
            Gecko Robotics was contracted to perform a Rapid Ultrasonic Gridding
            (RUG) Examination on the {mainInfo.assetType} ({mainInfo.assetName})
            at the {mainInfo.customerName} facility in{' '}
            {mainInfo.customerLocation}.
          </Text>

          <Text style={{ fontSize: 10, marginBottom: 8 }}>
            {mainInfo.customerName} identified {summaryData.coverageScope} for
            the RUG inspection. The inspection resolution is{' '}
            {formatResolution(summaryData.inspectionResolution)} and the
            deliverable resolution is{' '}
            {formatResolution(summaryData.deliverableResolution)}.
          </Text>

          <Text style={{ fontSize: 10, marginBottom: 8 }}>
            The material for the {mainInfo.assetType} is{' '}
            {mainInfo.assetMaterial} with a nominal thickness of{' '}
            {nominalRangeStr}. The surface temperature was{' '}
            {mainInfo.inspectionTemperature} at the time of inspection.
          </Text>
        </View>

        <View style={{ marginBottom: 16 }}>
          <Text
            style={{
              fontSize: 12,
              fontWeight: 'bold',
              marginBottom: 8,
              color: '#2e5266',
            }}>
            Inspection Summary:
          </Text>

          <Text style={{ fontSize: 10, marginBottom: 8 }}>
            {totalUTReadingsStr} The maximum wall loss detected was{' '}
            {formatMeasurement({
              measurement: maxWallLossPercent,
              minimumFractionDigits: 2,
              variant: 'narrow',
              maximumFractionDigits: 2,
            })}
            .
          </Text>
        </View>

        <View style={{ marginBottom: 16 }}>
          {summaryData.useTableForSummary ? (
            <SectionStatsTable assetSectionStats={assetSectionStats} />
          ) : (
            assetSectionStats.map((sectionStats) => (
              <View key={sectionStats.sectionName}>
                <SectionStatsText
                  sectionName={sectionStats.sectionName}
                  minThickness={sectionStats.minThickness}
                  averageThickness={sectionStats.averageThickness}
                  wallLossPercent={sectionStats.wallLossPercent}
                  nominalThickness={sectionStats.nominalThickness}
                />
              </View>
            ))
          )}
        </View>

        {summaryData.corrosionBLUF && (
          <View style={{ marginBottom: 16 }}>
            <Text
              style={{
                fontSize: 12,
                fontWeight: 'bold',
                marginBottom: 8,
                color: '#2e5266',
              }}>
              Corrosion Summary:
            </Text>
            <Text style={{ fontSize: 10 }}>{summaryData.corrosionBLUF}</Text>
          </View>
        )}

        {summaryData.inspectionSummaryNotes && (
          <View>
            <Text
              style={{
                fontSize: 10,
                fontWeight: 'bold',
                marginBottom: 4,
                color: '#2e5266',
              }}>
              Notes
            </Text>
            <Text style={{ fontSize: 10 }}>
              {summaryData.inspectionSummaryNotes}
            </Text>
          </View>
        )}
      </ReportPage>
      <AddendumPages
        sectionId={RugReportSectionId.SUMMARY}
        sectionTitle='Inspection Summary'
      />
    </>
  )
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<SummarySectionData>()
  const { formData, handleSummarySectionFinish, selectedAssetModel } =
    useRugReportOps()

  const initialValues = React.useMemo(
    () => ({
      ...formData.summarySectionData,
    }),
    [formData.summarySectionData]
  )

  return (
    <StyledForm
      form={form}
      name='rugSummaryForm'
      onFinish={handleSummarySectionFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item
        label='Coverage Scope'
        name='coverageScope'
        rules={[{ required: true, message: 'Please enter coverage scope' }]}>
        <AutoComplete
          options={(selectedAssetModel
            ? (ReportCoverageScopeOptions[selectedAssetModel?.data.type] ?? [])
            : []
          ).map((status) => {
            return { value: status }
          })}
          placeholder='Select or Type Scope'
          filterOption={autocompleteFilterOption}>
          <Input.TextArea rows={2} />
        </AutoComplete>
      </Form.Item>

      <Form.Item
        label='Display Summary as Table'
        name='useTableForSummary'
        valuePropName='checked'>
        <Switch />
      </Form.Item>

      <Form.Item
        label={
          <Space>
            <span>Corrosion BLUF</span>
            <Dropdown
              menu={{
                items: Object.keys(CORROSION_BLUF_TEMPLATES).map((key) => ({
                  key,
                  label: key,
                  onClick: () => {
                    const currentValue =
                      form.getFieldValue('corrosionBLUF') || ''
                    const templateText =
                      CORROSION_BLUF_TEMPLATES[
                        key as keyof typeof CORROSION_BLUF_TEMPLATES
                      ]
                    const newValue = currentValue
                      ? `${currentValue}\n\n${templateText}`
                      : templateText
                    form.setFieldValue('corrosionBLUF', newValue)
                  },
                })),
              }}
              trigger={['click']}>
              <span>
                <CantileverButton>Add Template</CantileverButton>
              </span>
            </Dropdown>
          </Space>
        }
        name='corrosionBLUF'
        rules={[{ required: true, message: 'Please enter corrosion BLUF' }]}>
        <Input.TextArea
          rows={6}
          placeholder='Bottom line up front summary of corrosion findings'
        />
      </Form.Item>

      <Form.Item label='Inspection Resolution'>
        <Space.Compact style={{ width: '100%' }}>
          <Form.Item
            name={['inspectionResolution', 'width']}
            noStyle
            rules={[{ required: true, message: 'Width required' }]}>
            <InputNumber style={{ width: 80 }} placeholder='Width' />
          </Form.Item>

          <Form.Item
            name={['inspectionResolution', 'widthUnit']}
            noStyle
            rules={[{ required: true, message: 'Unit required' }]}>
            <Select
              style={{ width: 80 }}
              placeholder='Unit'
              options={RESOLUTION_UNIT_OPTIONS}
            />
          </Form.Item>

          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px',
            }}>
            x
          </span>

          {selectedAssetModel?.data.type ===
            GeckoAssetModelType.LegacyBoiler && (
            <>
              <Form.Item name={['inspectionResolution', 'angles']} noStyle>
                <InputNumber style={{ width: 80 }} placeholder='Angles' />
              </Form.Item>

              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px',
                }}>
                angles x
              </span>
            </>
          )}

          <Form.Item
            name={['inspectionResolution', 'height']}
            noStyle
            rules={[{ required: true, message: 'Height required' }]}>
            <InputNumber style={{ width: 80 }} placeholder='Height' />
          </Form.Item>

          <Form.Item
            name={['inspectionResolution', 'heightUnit']}
            noStyle
            rules={[{ required: true, message: 'Unit required' }]}>
            <Select
              style={{ width: 80 }}
              placeholder='Unit'
              options={RESOLUTION_UNIT_OPTIONS}
            />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      <Form.Item label='Deliverable Resolution'>
        <Space.Compact style={{ width: '100%' }}>
          <Form.Item
            name={['deliverableResolution', 'width']}
            noStyle
            rules={[{ required: true, message: 'Width required' }]}>
            <InputNumber style={{ width: 80 }} placeholder='Width' />
          </Form.Item>

          <Form.Item
            name={['deliverableResolution', 'widthUnit']}
            noStyle
            rules={[{ required: true, message: 'Unit required' }]}>
            <Select
              style={{ width: 80 }}
              placeholder='Unit'
              options={RESOLUTION_UNIT_OPTIONS}
            />
          </Form.Item>

          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 8px',
            }}>
            x
          </span>

          {selectedAssetModel?.data.type ===
            GeckoAssetModelType.LegacyBoiler && (
            <>
              <Form.Item name={['deliverableResolution', 'angles']} noStyle>
                <InputNumber style={{ width: 80 }} placeholder='Angles' />
              </Form.Item>

              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 8px',
                }}>
                angles x
              </span>
            </>
          )}

          <Form.Item
            name={['deliverableResolution', 'height']}
            noStyle
            rules={[{ required: true, message: 'Height required' }]}>
            <InputNumber style={{ width: 80 }} placeholder='Height' />
          </Form.Item>

          <Form.Item
            name={['deliverableResolution', 'heightUnit']}
            noStyle
            rules={[{ required: true, message: 'Unit required' }]}>
            <Select
              style={{ width: 80 }}
              placeholder='Unit'
              options={RESOLUTION_UNIT_OPTIONS}
            />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      <Form.Item
        label='Inspection Summary Notes (Optional)'
        name='inspectionSummaryNotes'>
        <Input.TextArea
          rows={3}
          placeholder='Additional notes about the inspection...'
        />
      </Form.Item>

      <Form.Item>
        <CantileverButton
          primary
          onClick={() => form.submit()}
          style={{ width: '100%' }}>
          Save Inspection Summary
        </CantileverButton>
      </Form.Item>
    </StyledForm>
  )
}

export const InspectionSummary: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: RugReportSectionId.SUMMARY,
  description:
    'Inspection scope, summary statistics, and section-by-section breakdown',
  title: 'Inspection Summary',
  requiredFields: requiredSummaryPageFields,
}
